import type {
  ComputedJavaScriptVariableState,
  SetVariableNumberTypePayload,
} from '@unifyapps/defs/types/javascriptVariable';
import _isUndefined from 'lodash/isUndefined';

/**
 * Any change here should be reflected in the METHODS_DOCUMENTATION.md file
 */
export function getNextPageVariableStateForNumber({
  variableState,
  operationDetails,
  onError,
}: {
  variableState: ComputedJavaScriptVariableState<unknown>;
  operationDetails: {
    operation?: SetVariableNumberTypePayload['operationDetails']['operation'];
    value?: unknown;
  };
  onError?: (message: string) => void;
}) {
  // a bad payload is logged to the dev console and surfaced to Pulse (via onError) as an error,
  // then the variable is left unchanged. Only the invalid-payload branches call this.
  const reportPayloadError = (message: string) => {
    console.error(message);
    onError?.(message);
  };
  switch (operationDetails.operation) {
    case 'SET': {
      // SET allows undefined to reset the variable, or a valid number
      if (!_isUndefined(operationDetails.value)) {
        const numValue = Number(operationDetails.value);
        if (Number.isNaN(numValue)) {
          reportPayloadError(
            'operationDetails.value should be a valid number or undefined for set operation',
          );
          return variableState;
        }
        return {
          ...variableState,
          value: numValue,
        };
      }
      // allow setting to undefined
      return {
        ...variableState,
        value: undefined,
      };
    }
    case 'INCREMENT_BY': {
      // validate current state is number or undefined (for initialization)
      if (!_isUndefined(variableState.value) && Number.isNaN(Number(variableState.value))) {
        reportPayloadError('variableState.value should be number or undefined for increment operation');
        return variableState;
      }
      // validate input value is a valid number
      if (_isUndefined(operationDetails.value) || Number.isNaN(Number(operationDetails.value))) {
        reportPayloadError(
          'operationDetails.value is required and should be a valid number for increment operation',
        );
        return variableState;
      }
      return {
        ...variableState,
        value: Number(variableState.value ?? 0) + Number(operationDetails.value),
      };
    }
    case 'DECREMENT_BY': {
      // validate current state is number or undefined (for initialization)
      if (!_isUndefined(variableState.value) && Number.isNaN(Number(variableState.value))) {
        reportPayloadError('variableState.value should be number or undefined for decrement operation');
        return variableState;
      }
      // validate input value is a valid number
      if (_isUndefined(operationDetails.value) || Number.isNaN(Number(operationDetails.value))) {
        reportPayloadError(
          'operationDetails.value is required and should be a valid number for decrement operation',
        );
        return variableState;
      }
      return {
        ...variableState,
        value: Number(variableState.value ?? 0) - Number(operationDetails.value),
      };
    }
    default:
      return variableState;
  }
}
